import {
  ONBOARDING_PARAM_EXIT_POINT,
  ONBOARDING_PARAM_CLASSROOM_SESSION_ID,
  ONBOARDING_PARAM_OFFER,
  LS_KEY_ONBOARDING_DUEL_IDS,
} from './constants';
import { type Maybe } from '@/graphql';

type RoundWithUserQuestions = {
  userQuestions?: Array<{
    userAnswer?: unknown;
  }>;
};

const getCompletedRoundsCount = (rounds?: Maybe<Array<Maybe<RoundWithUserQuestions>>>): number => {
  if (!rounds || rounds.length === 0) return 0;

  let completedCount = 0;
  for (const round of rounds) {
    if (!round?.userQuestions) continue;

    const allQuestionsAnswered = round.userQuestions.every(
      (q) => q && q.userAnswer !== null && q.userAnswer !== undefined
    );

    if (allQuestionsAnswered) {
      completedCount++;
    }
  }

  return completedCount;
};

/**
 * Builds the URL for navigating to the onboarding completion screen
 * with the appropriate tracking parameters
 */
export const buildOnboardingCompletionUrl = (
  params: {
    rounds?: Maybe<Array<Maybe<RoundWithUserQuestions>>>;
    sessionId?: Maybe<string>;
  },
  isSpecialist?: boolean
): string => {
  const { rounds, sessionId } = params;

  const exitPoint = getCompletedRoundsCount(rounds) * 3;

  const urlParams = new URLSearchParams();
  if (sessionId) {
    urlParams.set(ONBOARDING_PARAM_CLASSROOM_SESSION_ID, sessionId);
  }
  urlParams.set(ONBOARDING_PARAM_EXIT_POINT, exitPoint.toString());

  const onboardingTarget = isSpecialist ? `/onboard-final` : '/onboard-keep-learning';

  return `${onboardingTarget}?${urlParams.toString()}`;
};

export const buildOfferReturnUrl = ({
  classroomSessionId,
  exitPoint,
  offer,
}: {
  classroomSessionId?: string | null;
  exitPoint?: string | null;
  offer: string;
}): string => {
  const urlParams = new URLSearchParams();
  if (classroomSessionId) {
    urlParams.set(ONBOARDING_PARAM_CLASSROOM_SESSION_ID, classroomSessionId);
  }
  if (exitPoint) urlParams.set(ONBOARDING_PARAM_EXIT_POINT, exitPoint);
  urlParams.set(ONBOARDING_PARAM_OFFER, offer);

  return `/onboard-final?${urlParams.toString()}`;
};

/**
 * Keyed per user: the list decides which offer a user is eligible for, so a second account on
 * the same browser must not inherit the first one's duels.
 */
const onboardingDuelIdsKey = (userId: string): string =>
  `${LS_KEY_ONBOARDING_DUEL_IDS}:${userId}`;

export const readStoredOnboardingDuelIds = (userId?: string | null): string[] => {
  if (!userId) return [];

  try {
    // The unscoped key was shared by every account on the device. Drop it on sight.
    localStorage.removeItem(LS_KEY_ONBOARDING_DUEL_IDS);

    const parsed = JSON.parse(localStorage.getItem(onboardingDuelIdsKey(userId)) ?? '[]');

    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

export const storeOnboardingDuelIds = (userId: string | null | undefined, ids: string[]): void => {
  if (!userId) return;

  try {
    localStorage.setItem(onboardingDuelIdsKey(userId), JSON.stringify(ids));
  } catch {
    // Private mode or a full quota: the server count still holds within the current month.
  }
};

/** Called on sign-out so the next account on this browser starts from the server's count. */
export const clearStoredOnboardingDuelIds = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key === LS_KEY_ONBOARDING_DUEL_IDS || key.startsWith(`${LS_KEY_ONBOARDING_DUEL_IDS}:`))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing readable to clear.
  }
};

export const markOfferViewTracked = (key: string): boolean => {
  const storageKey = `offerViewed:${key}`;
  try {
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // No sessionStorage: fall back to counting the view rather than losing it entirely.
  }

  return true;
};
